import { useEffect, useRef, useState } from "react";

export type ViewMode = "basic" | "adaptive";

type ViewSettingsMenuProps = {
  mode: ViewMode;
  showRemoteCursors: boolean;
  simulateRandomCursors: boolean;
  onSelectMode: (mode: ViewMode) => void;
  onToggleRemoteCursors: () => void;
  onToggleSimulateRandomCursors: () => void;
};

export function ViewSettingsMenu({
  mode,
  showRemoteCursors,
  simulateRandomCursors,
  onSelectMode,
  onToggleRemoteCursors,
  onToggleSimulateRandomCursors
}: ViewSettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const [devMenuOpen, setDevMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setDevMenuOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!rootRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="settings-menu" ref={rootRef}>
      <button
        className="ghost settings-trigger"
        type="button"
        aria-label="Open view settings"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span aria-hidden="true">⚙</span>
      </button>
      {open && (
        <div className="settings-popover" role="menu" aria-label="View settings">
          <button
            className={`settings-item ${mode === "basic" ? "is-active" : ""}`}
            type="button"
            role="menuitemradio"
            aria-checked={mode === "basic"}
            onClick={() => {
              onSelectMode("basic");
              setOpen(false);
            }}
          >
            Basic Mode
          </button>
          <button
            className={`settings-item ${mode === "adaptive" ? "is-active" : ""}`}
            type="button"
            role="menuitemradio"
            aria-checked={mode === "adaptive"}
            onClick={() => {
              onSelectMode("adaptive");
              setOpen(false);
            }}
          >
            Adaptive Mode
          </button>
          <label className="settings-item settings-toggle">
            <span>Show or hide other players&apos; cursor</span>
            <input
              type="checkbox"
              checked={showRemoteCursors}
              onChange={onToggleRemoteCursors}
            />
          </label>
          <div
            className="settings-submenu-root"
            onMouseEnter={() => setDevMenuOpen(true)}
            onMouseLeave={(event) => {
              const next = event.relatedTarget as Node | null;
              if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
                setDevMenuOpen(false);
              }
            }}
            onFocusCapture={() => setDevMenuOpen(true)}
            onBlurCapture={(event) => {
              const next = event.relatedTarget as Node | null;
              if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
                setDevMenuOpen(false);
              }
            }}
          >
            <button
              className={`settings-item settings-submenu-trigger ${
                devMenuOpen ? "is-active" : ""
              }`}
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={devMenuOpen}
              onClick={() => setDevMenuOpen((prev) => !prev)}
            >
              <span>Dev Mode</span>
              <span aria-hidden="true">›</span>
            </button>
            {devMenuOpen && (
              <div className="settings-submenu" role="menu" aria-label="Dev Mode">
                <label className="settings-item settings-toggle">
                  <span>Simulate random cursor positions</span>
                  <input
                    type="checkbox"
                    checked={simulateRandomCursors}
                    onChange={onToggleSimulateRandomCursors}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
